/**
 * A PIN gate in front of the branding editor.
 *
 * This is a deterrent, not real access control: everything here is
 * client-side with no server to enforce anything, so anyone with the
 * browser's dev tools open can read/overwrite localStorage directly and
 * bypass it entirely. It stops someone from casually poking around the
 * branding screen; it cannot stop a determined bypass. Genuine protection
 * against that would need a backend that gatekeeps the write, which this
 * offline-first app doesn't have.
 */

const PIN_HASH_KEY = 'mahjong-scorer:brandingPinHash';
const PIN_ATTEMPTS_KEY = 'mahjong-scorer:brandingPinAttempts';
const PIN_LOCK_UNTIL_KEY = 'mahjong-scorer:brandingPinLockUntil';

/** Wrong guesses allowed before a lockout kicks in. */
export const PIN_MAX_ATTEMPTS = 5;
/** How long a lockout lasts once triggered. */
export const PIN_LOCKOUT_MS = 60_000;

async function hashPin(pin: string): Promise<string> {
  const data = new TextEncoder().encode(pin);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function hasPinSet(): boolean {
  return localStorage.getItem(PIN_HASH_KEY) !== null;
}

export async function setPin(pin: string): Promise<void> {
  localStorage.setItem(PIN_HASH_KEY, await hashPin(pin));
  clearPinLockout();
}

/** Milliseconds remaining on an active lockout, or 0 if not locked out. */
export function pinLockoutRemainingMs(): number {
  const lockUntil = Number(localStorage.getItem(PIN_LOCK_UNTIL_KEY) ?? 0);
  return Math.max(0, lockUntil - Date.now());
}

function clearPinLockout(): void {
  localStorage.removeItem(PIN_ATTEMPTS_KEY);
  localStorage.removeItem(PIN_LOCK_UNTIL_KEY);
}

/**
 * A guess counter alongside the PIN itself — the doc comment above still
 * applies (this is a deterrent, not real access control; someone with
 * devtools open can reset these same localStorage keys). It's here to raise
 * the bar against someone just sitting and typing PINs at the on-screen
 * keypad, which the plain hash-compare didn't stop at all.
 */
export async function verifyPin(pin: string): Promise<boolean> {
  if (pinLockoutRemainingMs() > 0) return false;

  const stored = localStorage.getItem(PIN_HASH_KEY);
  if (!stored) return false;

  if ((await hashPin(pin)) === stored) {
    clearPinLockout();
    return true;
  }

  const attempts = Number(localStorage.getItem(PIN_ATTEMPTS_KEY) ?? 0) + 1;
  if (attempts >= PIN_MAX_ATTEMPTS) {
    localStorage.setItem(PIN_LOCK_UNTIL_KEY, String(Date.now() + PIN_LOCKOUT_MS));
    localStorage.removeItem(PIN_ATTEMPTS_KEY);
  } else {
    localStorage.setItem(PIN_ATTEMPTS_KEY, String(attempts));
  }
  return false;
}
